import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  ShoppingCart, Plus, Truck, FileText, Package,
  Search, Loader2, CheckCircle2, Clock, AlertCircle,
  ChevronDown, Trash2, Edit2, Building2,
} from 'lucide-react';
import { api } from '@/lib/api';
import { clsx } from 'clsx';

const TABS = ['purchase_orders', 'vendors', 'grn'] as const;
type Tab = typeof TABS[number];

const PO_STATUS = {
  draft:     { label: 'Draft',    color: 'bg-gray-100 text-gray-600' },
  sent:      { label: 'Sent',     color: 'bg-blue-100 text-blue-700' },
  partial:   { label: 'Partial',  color: 'bg-yellow-100 text-yellow-700' },
  received:  { label: 'Received', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled',color: 'bg-red-100 text-red-500' },
};

function VendorsTab() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => api.get('/inventory/vendors').then(r => r.data),
  });

  const [form, setForm] = useState({
    vendor_name: '', vendor_code: '', vendor_type: 'pharma', contact_person: '',
    mobile: '', email: '', address: '', gst_number: '', drug_license: '', credit_days: 30,
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/inventory/vendors', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['vendors'] }); setShowNew(false); },
  });

  const vendors = (data ?? []).filter((v: any) =>
    !search || v.vendor_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 flex-1 max-w-xs">
          <Search size={13} className="text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search vendors..." className="bg-transparent text-sm outline-none flex-1" />
        </div>
        <button onClick={() => setShowNew(true)}
          className="flex items-center gap-2 bg-orange text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-orange/90 transition">
          <Plus size={14} /> Add Vendor
        </button>
      </div>

      {showNew && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-4">
          <h3 className="font-semibold text-navy mb-3">New Vendor</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { key: 'vendor_name', label: 'Vendor Name *', placeholder: 'e.g. MedPlus Pharma' },
              { key: 'vendor_code', label: 'Code *', placeholder: 'VEN-001' },
              { key: 'contact_person', label: 'Contact Person', placeholder: '' },
              { key: 'mobile', label: 'Mobile', placeholder: '' },
              { key: 'gst_number', label: 'GST Number', placeholder: '' },
              { key: 'drug_license', label: 'Drug License', placeholder: '' },
            ].map(f => (
              <div key={f.key}>
                <label className="text-xs font-semibold text-gray-600 block mb-1">{f.label}</label>
                <input value={(form as any)[f.key]} onChange={e => setForm(fm => ({ ...fm, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
              </div>
            ))}
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Type</label>
              <select value={form.vendor_type} onChange={e => setForm(f => ({ ...f, vendor_type: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none">
                {['pharma','surgical','general','equipment','lab_reagent','other'].map(t => (
                  <option key={t} value={t} className="capitalize">{t.replace('_',' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Credit Days</label>
              <input type="number" value={form.credit_days} onChange={e => setForm(f => ({ ...f, credit_days: Number(e.target.value) }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
            </div>
            <div className="col-span-full">
              <label className="text-xs font-semibold text-gray-600 block mb-1">Address</label>
              <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange/30 outline-none" />
            </div>
          </div>
          <div className="flex gap-3 mt-3">
            <button onClick={() => setShowNew(false)} className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 transition">Cancel</button>
            <button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending || !form.vendor_name}
              className="px-5 py-2 bg-orange text-white rounded-xl text-sm font-semibold disabled:opacity-40 hover:bg-orange/90 flex items-center gap-2 transition">
              {createMutation.isPending && <Loader2 size={13} className="animate-spin" />} Save Vendor
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-10"><Loader2 size={20} className="animate-spin text-orange" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                {['Vendor','Code','Type','Contact','Mobile','GST','Credit Days'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {vendors.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-gray-400">No vendors added yet</td></tr>
              ) : vendors.map((v: any) => (
                <tr key={v.id} className="border-b border-gray-50 hover:bg-gray-50/40 transition">
                  <td className="px-4 py-3 font-medium text-navy">{v.vendor_name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{v.vendor_code}</td>
                  <td className="px-4 py-3 capitalize text-gray-500 text-xs">{v.vendor_type?.replace('_',' ')}</td>
                  <td className="px-4 py-3 text-gray-600">{v.contact_person ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{v.mobile ?? '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{v.gst_number ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{v.credit_days} days</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PurchaseOrdersTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['purchase-orders'],
    queryFn: () => api.get('/inventory/purchase-orders').then(r => r.data),
  });

  const orders = data?.orders ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button className="flex items-center gap-2 bg-orange text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-orange/90 transition">
          <Plus size={14} /> New Purchase Order
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10"><Loader2 size={20} className="animate-spin text-orange" /></div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card flex flex-col items-center justify-center py-16 text-gray-400">
          <ShoppingCart size={36} className="mb-2 opacity-25" />
          <p className="font-medium text-navy">No purchase orders yet</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                {['PO Number','Vendor','Type','Date','Items','Total (₹)','Status'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((po: any) => {
                const statusCfg = PO_STATUS[po.status as keyof typeof PO_STATUS] ?? PO_STATUS.draft;
                return (
                  <tr key={po.id} className="border-b border-gray-50 hover:bg-gray-50/40 transition">
                    <td className="px-4 py-3 font-mono text-xs text-navy font-semibold">{po.po_number}</td>
                    <td className="px-4 py-3 text-gray-700">{po.vendor_name}</td>
                    <td className="px-4 py-3 capitalize text-gray-500 text-xs">{po.po_type?.replace('_',' ')}</td>
                    <td className="px-4 py-3 text-gray-500">{format(new Date(po.po_date), 'dd MMM yyyy')}</td>
                    <td className="px-4 py-3 text-gray-600">{po.item_count ?? 0}</td>
                    <td className="px-4 py-3 font-semibold text-navy">₹{Number(po.total_amount).toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3">
                      <span className={clsx('text-xs px-2.5 py-1 rounded-full font-medium', statusCfg.color)}>{statusCfg.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function GrnTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['grn-list'],
    queryFn: () => api.get('/inventory/grn').then(r => r.data),
  });

  const grns = data?.grns ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button className="flex items-center gap-2 bg-orange text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-orange/90 transition">
          <Plus size={14} /> Record GRN
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10"><Loader2 size={20} className="animate-spin text-orange" /></div>
      ) : grns.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card flex flex-col items-center justify-center py-16 text-gray-400">
          <Truck size={36} className="mb-2 opacity-25" />
          <p className="font-medium text-navy">No goods receipts recorded</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                {['GRN No.','Vendor','Date','Invoice No.','Amount (₹)','Received By','Status'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grns.map((g: any) => (
                <tr key={g.id} className="border-b border-gray-50 hover:bg-gray-50/40 transition">
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-navy">{g.grn_number}</td>
                  <td className="px-4 py-3 text-gray-700">{g.vendor_name}</td>
                  <td className="px-4 py-3 text-gray-500">{format(new Date(g.grn_date), 'dd MMM yyyy')}</td>
                  <td className="px-4 py-3 text-gray-600">{g.invoice_number ?? '—'}</td>
                  <td className="px-4 py-3 font-semibold text-navy">₹{Number(g.invoice_amount ?? 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-gray-600">{g.received_by_name}</td>
                  <td className="px-4 py-3">
                    <span className={clsx('text-xs px-2.5 py-1 rounded-full font-medium',
                      g.status === 'posted' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700')}>
                      {g.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function InventoryPage() {
  const [tab, setTab] = useState<Tab>('vendors');

  const tabLabels: Record<Tab, string> = {
    vendors: 'Vendors',
    purchase_orders: 'Purchase Orders',
    grn: 'Goods Receipt (GRN)',
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
            <ShoppingCart size={22} className="text-orange" /> Inventory & Purchase
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Vendor management, purchase orders and goods receipt</p>
        </div>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-5 w-fit">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={clsx('px-4 py-1.5 rounded-lg text-sm font-medium transition-all',
              tab === t ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-gray-700')}>
            {tabLabels[t]}
          </button>
        ))}
      </div>

      {tab === 'vendors'         && <VendorsTab />}
      {tab === 'purchase_orders' && <PurchaseOrdersTab />}
      {tab === 'grn'             && <GrnTab />}
    </div>
  );
}
